<script setup>
import { computed } from 'vue'
import { SYSTEM_NOTIFICATION_SEVERITY_CONFIG } from '@/constants/systemNotification.js'
import BaseButton from '@/components/BaseButton.vue'
import ModalContent from '@/components/ModalContent.vue'

const props = defineProps({
  isShowing: { required: true, type: Boolean },
  userName: { required: false, type: String, default: '' },
  totalCount: { required: true, type: Number },
  severityCounts: { required: true, type: Array }
})

const emit = defineEmits(['close', 'review'])

const categories = computed(() =>
  props.severityCounts.map(item => ({ ...item, ...SYSTEM_NOTIFICATION_SEVERITY_CONFIG[item.severity] }))
)
</script>

<template>
  <modal-content
    class="system-notification-welcome"
    modal-size="system-notification-welcome__dialog"
    header-class="system-notification-welcome__header"
    modal-body-class="system-notification-welcome__body"
    footer-class="system-notification-welcome__footer"
    close-button-class="system-notification-welcome__close"
    :is-showing="isShowing"
    @close="emit('close')"
  >
    <template #header>
      <div class="system-notification-welcome__title">
        <i class="mdi mdi-bell-outline system-notification-welcome__bell" aria-hidden="true" />
        <h4 class="system-notification-welcome__title-text">
          {{ $t('components.system_notification_modal.welcome.title', { name: userName }) }}
        </h4>
      </div>
    </template>

    <template #body>
      <p class="system-notification-welcome__summary">
        {{ $t('components.system_notification_modal.welcome.summary', { count: totalCount }) }}
      </p>

      <ul class="system-notification-welcome__categories">
        <li v-for="item in categories" :key="item.severity" class="system-notification-welcome__category">
          <span
            class="system-notification-welcome__count"
            :class="`system-notification-severity--${item.modifier}`"
          >
            {{ item.count }}
          </span>
          <span class="system-notification-welcome__label">
            {{ $t(item.categoryKey) }}
          </span>
        </li>
      </ul>

      <div class="system-notification-welcome__info">
        <i class="mdi mdi-information-outline system-notification-welcome__info-icon" aria-hidden="true" />
        <p class="system-notification-welcome__info-text">
          {{ $t('components.system_notification_modal.welcome.info') }}
        </p>
      </div>
    </template>

    <template #footer>
      <base-button item-class="btn-outline-secondary system-notification-welcome__button" @click="emit('close')">
        {{ $t('components.system_notification_modal.close') }}
      </base-button>
      <base-button item-class="btn-primary system-notification-welcome__button" @click="emit('review')">
        {{ $t('components.system_notification_modal.welcome.review') }}
      </base-button>
    </template>
  </modal-content>
</template>

<style scoped>
:deep(.system-notification-welcome__dialog) {
  --bs-modal-width: 500px;

  .modal-content {
    min-height: 460px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
  }

  .modal-header.system-notification-welcome__header {
    position: relative;
    justify-content: center;
    padding: 36px 16px 0;
    border-bottom: 0;
  }

  .system-notification-welcome__close {
    position: absolute;
    top: 24px;
    right: 24px;
    padding: 0;
  }

  .modal-body.system-notification-welcome__body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 32px;
  }

  .modal-footer.system-notification-welcome__footer {
    flex-wrap: nowrap;
    gap: 12px;
    margin: 0 !important;
    padding: 24px 32px 36px;
    border-top: 0;

    > * {
      margin: 0;
    }
  }
}

.system-notification-welcome__title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
}

.system-notification-welcome__bell {
  height: 56px;
  font-size: 56px;
  line-height: 56px;
  color: var(--primary);
}

.system-notification-welcome__title-text {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  text-align: center;
  color: var(--neutral-950);
  word-break: break-word;
}

.system-notification-welcome__summary {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: var(--gray-800);
}

.system-notification-welcome__categories {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.system-notification-welcome__category {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 210px;
  height: 60px;
  padding: 8px 16px;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  background-color: var(--white);
  text-align: center;
}

@media (max-width: 575.98px) {
  .system-notification-welcome__categories {
    flex-wrap: wrap;
    gap: 8px;
  }

  .system-notification-welcome__category {
    flex-basis: calc(50% - 4px);
    max-width: none;
  }
}

.system-notification-welcome__count {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.system-notification-welcome__label {
  width: 100%;
  overflow: hidden;
  font-size: 14.222px;
  font-weight: 500;
  line-height: 18px;
  color: var(--secondary-active);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.system-notification-welcome__info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.system-notification-welcome__info-icon {
  font-size: 16px;
  line-height: 1;
  color: var(--secondary-active);
}

.system-notification-welcome__info-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: 0.1px;
  color: var(--secondary-active);
}

.system-notification-welcome__button {
  flex: 1 1 0;
  height: 40px;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 14.222px;
  font-weight: 500;
  line-height: 1.12;
}
</style>
